import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReportStudyData, ReportUserData } from "../models";
import type { RootState } from "../store";
import { downloadAudit } from "../api";

export interface AuditTrailState {
  readonly studyData?: ReportStudyData;
  readonly userData?: ReportUserData;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly requestingCsv: boolean;
  readonly errorMessage?: string;
}

export const initialState: AuditTrailState = {
  studyData: undefined,
  userData: undefined,
  startDate: undefined,
  endDate: undefined,
  requestingCsv: false,
  errorMessage: undefined
};

// thunks
export const requestAuditTrailPdf = createAsyncThunk(
  "auditTrail/requestAuditTrailPdf",
  async (_: void, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    const response = await downloadAudit(
      "pdf",
      state.auditTrail.studyData,
      state.auditTrail.userData,
      state.auditTrail.startDate,
      state.auditTrail.endDate
    );
    return response;
  }
);

export const requestAuditTrailCsv = createAsyncThunk(
  "auditTrail/requestAuditTrailCsv",
  async (_: void, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    const response = await downloadAudit(
      "csv",
      state.auditTrail.studyData,
      state.auditTrail.userData,
      state.auditTrail.startDate,
      state.auditTrail.endDate
    );
    return response;
  }
);

export const auditTrailSlice = createSlice({
  name: "auditTrail",
  initialState: initialState,
  reducers: {
    clearAuditTrailState: _ => {
      return initialState;
    },
    setAuditTrailStudyData: (state, action: PayloadAction<ReportStudyData | undefined>) => {
      state.studyData = action.payload || undefined;
    },
    setAuditTrailUserData: (state, action: PayloadAction<ReportUserData | undefined>) => {
      state.userData = action.payload || undefined;
    },
    setAuditTrailDateRange: (
      state,
      action: PayloadAction<{ startDate?: string; endDate?: string }>
    ) => {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    }
  },
  extraReducers: builder => {
    builder.addCase(requestAuditTrailCsv.pending, state => {
      state.requestingCsv = true;
      state.errorMessage = undefined;
    });
    builder.addCase(requestAuditTrailCsv.fulfilled, state => {
      state.requestingCsv = false;
    });
    builder.addCase(requestAuditTrailCsv.rejected, (state, action) => {
      state.requestingCsv = false;
      state.errorMessage = action.error.message || "Failed to generate audit trail CSV";
    });
    builder.addCase(requestAuditTrailPdf.pending, state => {
      state.requestingCsv = true;
      state.errorMessage = undefined;
    });
    builder.addCase(requestAuditTrailPdf.fulfilled, state => {
      state.requestingCsv = false;
    });
    builder.addCase(requestAuditTrailPdf.rejected, (state, action) => {
      state.requestingCsv = false;
      state.errorMessage = action.error.message || "Failed to generate audit trail PDF";
    });
  }
});

export const {
  clearAuditTrailState,
  setAuditTrailStudyData,
  setAuditTrailUserData,
  setAuditTrailDateRange
} = auditTrailSlice.actions;

export default auditTrailSlice.reducer;
